<script lang="ts">
import { useCustomerSafetyStore } from '@/stores/customer-safety'
import type { CustomerPlayExclusion, CustomerSpendingLimit } from '@/types'
import {
  isCustomerPlayExclusion,
  isCustomerSpendingLimit,
} from '@/utils/type-guard-utils'
import { type PropType, defineComponent } from 'vue'
import { POSITION, TYPE } from 'vue-toastification'

export const CUSTOMER_SAFETY_TOAST_KEY = 'customer-safety-toast'

export const CUSTOMER_SAFETY_TOAST_OPTIONS = Object.freeze({
  id: CUSTOMER_SAFETY_TOAST_KEY,
  type: TYPE.INFO,
  timeout: false,
  closeOnClick: true,
  hideProgressBar: true,
  position: POSITION.TOP_CENTER,
  toastClassName: CUSTOMER_SAFETY_TOAST_KEY,
} as const)

/**
 * CustomerSafetyToast
 */
export default defineComponent({
  props: {
    safetyMeasure: {
      type: Object as PropType<CustomerPlayExclusion | CustomerSpendingLimit>,
      required: true,
    },
  },
  setup() {
    return {
      isCustomerSpendingLimit,
      isCustomerPlayExclusion,
    }
  },
  beforeMounted() {
    this.$toast.dismiss(CUSTOMER_SAFETY_TOAST_KEY)
  },
  mounted() {
    // Update the store with the latest safety measure data.
    const customerSafetyStore = useCustomerSafetyStore()

    customerSafetyStore.customerSafety.set(
      this.safetyMeasure.__type,
      this.safetyMeasure
    )
  },
})
</script>

<template>
  <div class="flex flex-col gap-1 rounded-md">
    <p class="text-slate-700">
      You currently have a
      {{
        isCustomerSpendingLimit(safetyMeasure)
          ? 'spending limit'
          : 'play exclusion'
      }}
      on your account.
    </p>

    <div v-if="isCustomerSpendingLimit(safetyMeasure)">
      You have
      <strong>
        {{
          $n(
            (safetyMeasure.spending_limit - safetyMeasure.current_spend) / 100,
            'currency'
          )
        }}
      </strong>
      remaining.
    </div>

    <div v-else-if="isCustomerPlayExclusion(safetyMeasure)">
      Your self-exclusion is until
      <strong>
        {{ $d(safetyMeasure.ends_at.toJSDate(), 'long') }},
        {{ $d(safetyMeasure.ends_at.toJSDate(), 'time') }} </strong
      >.
    </div>
  </div>
</template>

<style lang="scss">
.customer-safety-toast {
  @apply bg-transparent p-4 bg-red-300 text-black !important;

  .Vue-Toastification__close-button {
    @apply text-black opacity-100;
  }
}
</style>
